import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { useAuth } from '@pesto/features';
import { LoadingSpinner } from '@pesto/ui';
import { lazy, Suspense, type ReactNode } from 'react';
import { Route } from 'react-router-dom';

import { UserRole } from '../__generated__/graphql/api';
import { DevRoute } from '../components/DevRoute';
import { HomeRoute } from '../components/HomeRoute';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { RoleProtectedRoute } from '../components/RoleProtectedRoute';
import { routes } from '../constants/routes';
import { SCApplicationLayout } from '../layouts/SCApplicationLayout';

const AchRequests = lazy(() => import('../pages/AchRequests/AchRequests').then((m) => ({ default: m.AchRequests })));

const AdminActions = lazy(() =>
  import('../pages/AdminActions/AdminActions').then((m) => ({ default: m.AdminActions })),
);

const CardDetailsPage = lazy(() =>
  import('../pages/CardDetails/CardDetailsPage').then((m) => ({ default: m.CardDetailsPage })),
);

const Cards = lazy(() => import('../pages/Cards/Cards').then((m) => ({ default: m.Cards })));

const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));

const Transactions = lazy(() =>
  import('../pages/DeprecatedTransactions/DeprecatedTransactions').then((m) => ({ default: m.Transactions })),
);

const Devices = lazy(() => import('../pages/Devices/Devices').then((m) => ({ default: m.Devices })));

const PendingDrawdowns = lazy(() =>
  import('../pages/PendingDrawdowns/PendingDrawdowns').then((m) => ({ default: m.PendingDrawdowns })),
);

const LoginCallback = lazy(() => import('../pages/LoginCallback'));

const LoginPage = lazy(() => import('../pages/LoginPage').then((m) => ({ default: m.LoginPage })));

const NotFound = lazy(() => import('../pages/NotFound').then((m) => ({ default: m.NotFound })));

const ObanJobDetails = lazy(() =>
  import('../pages/ObanJobs/ObanJobDetails').then((m) => ({ default: m.ObanJobDetails })),
);

const ObanJobs = lazy(() => import('../pages/ObanJobs/ObanJobs').then((m) => ({ default: m.ObanJobs })));

const ProgramDetails = lazy(() =>
  import('../pages/ProgramDetails/ProgramDetails').then((m) => ({ default: m.ProgramDetails })),
);

const Programs = lazy(() => import('../pages/Programs/Programs').then((m) => ({ default: m.Programs })));

const PrimeRates = lazy(() => import('../pages/PrimeRates/PrimeRates').then((m) => ({ default: m.PrimeRates })));

const Reconciliation = lazy(() =>
  import('../pages/Reconciliation/Reconciliation').then((m) => ({ default: m.Reconciliation })),
);

const Partners = lazy(() => import('../pages/Partners/Partners').then((m) => ({ default: m.Partners })));

const Disputes = lazy(() => import('../pages/Disputes/Disputes').then((m) => ({ default: m.Disputes })));

const PlatformAccounts = lazy(() =>
  import('../pages/PlatformAccounts/PlatformAccounts').then((m) => ({ default: m.PlatformAccounts })),
);

const LedgerTransactions = lazy(() =>
  import('../pages/LedgerTransactions/LedgerTransactions').then((m) => ({ default: m.LedgerTransactions })),
);

const LoanData = lazy(() => import('../pages/LoanData/LoanData').then((m) => ({ default: m.LoanData })));

const LedgerTransactionDetails = lazy(() =>
  import('../pages/LedgerTransactions/LedgerTransactionDetails').then((m) => ({
    default: m.LedgerTransactionDetails,
  })),
);

const Reports = lazy(() => import('../pages/Reports/Reports').then((m) => ({ default: m.Reports })));

const UserSearch = lazy(() => import('../pages/SecureSearch/UserSearch').then((m) => ({ default: m.UserSearch })));

const StatementStudio = lazy(() =>
  import('../pages/StatementStudio/StatementStudio').then((m) => ({ default: m.StatementStudio })),
);

const StatementStudioJobDetails = lazy(() =>
  import('../pages/StatementStudioJobDetails/StatementStudioJobDetails').then((m) => ({
    default: m.StatementStudioJobDetails,
  })),
);

const TimelineRuns = lazy(() =>
  import('../pages/TimelineRuns/TimelineRuns').then((m) => ({ default: m.TimelineRuns })),
);

const Statments = lazy(() => import('../pages/Statements/Statements').then((m) => ({ default: m.Statments })));

const StatementsDetails = lazy(() =>
  import('../pages/StatementsDetails/StatementsDetails').then((m) => ({ default: m.StatementsDetails })),
);

const UserDetailsPage = lazy(() =>
  import('../pages/UserDetails/UserDetailsPage').then((m) => ({ default: m.UserDetailsPage })),
);

const Users = lazy(() => import('../pages/Users/Users').then((m) => ({ default: m.Users })));

const Loans = lazy(() => import('../pages/Loans/Loans').then((m) => ({ default: m.Loans })));

const LoanDetailsPage = lazy(() =>
  import('../pages/LoanDetails/LoanDetailsPage').then((m) => ({ default: m.LoanDetailsPage })),
);

const Admins = lazy(() => import('../pages/Admins/Admins').then((m) => ({ default: m.Admins })));

const VelocityControlsPage = lazy(() =>
  import('../pages/VelocityControls/VelocityControlsPage').then((m) => ({ default: m.VelocityControlsPage })),
);

const PageLoader = () => (
  <div className="flex h-full items-center justify-center">
    <LoadingSpinner size={32} />
  </div>
);

const AppRoutesProvider = ({ children }: { children: ReactNode }) => {
  return <SCApplicationLayout>{children}</SCApplicationLayout>;
};

export const RouterProvider = () => {
  const { isAuthenticated } = useAuth();

  return (
    <AppRoutesProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="*" element={<NotFound />} />
          <Route index element={<LoginPage />} />
          <Route path={routes.loginCallback} element={<LoginCallback />} />
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated()} />}>
            <Route path={routes.home} element={<HomeRoute />} />
            <Route path={routes.userSearch} element={<UserSearch />} />
            <Route path={`${routes.users}/:id/statements/:statementId`} element={<UserDetailsPage />} />
            <Route path={`${routes.users}/:id/:tab?`} element={<UserDetailsPage />} />
            <Route element={<RoleProtectedRoute allowedRoles={[UserRole.SuperAdmin]} />}>
              <Route path={routes.achRequests} element={<AchRequests />} />
              <Route path={routes.pendingDrawdowns} element={<PendingDrawdowns />} />
              <Route path={routes.statements} element={<Statments />} />
              <Route path={`${routes.statements}/:id?`} element={<StatementsDetails />} />
              <Route path={routes.dashboard} element={<Dashboard />} />
              <Route path={routes.users} element={<Users />} />
              <Route path={routes.loans} element={<Loans />} />
              <Route path={`${routes.loans}/:id/statements/:statementId`} element={<LoanDetailsPage />} />
              <Route path={`${routes.loans}/:id/:tab?`} element={<LoanDetailsPage />} />
              <Route path={routes.admins} element={<Admins />} />
              <Route path={routes.cards} element={<Cards />} />
              <Route path={`${routes.cards}/:cardId?/:tab?`} element={<CardDetailsPage />} />
              <Route path={routes.transactions} element={<Transactions />} />
              <Route path={routes.devices} element={<Devices />} />
              <Route path={routes.velocityControls} element={<VelocityControlsPage />} />
              <Route path={routes.programs} element={<Programs />} />
              <Route path={`${routes.programs}/:id`} element={<ProgramDetails />} />
              <Route path={routes.partners} element={<Partners />} />
              <Route path={routes.primeRates} element={<PrimeRates />} />
              <Route path={routes.reconciliation} element={<Reconciliation />} />
              <Route path={routes.disputes} element={<Disputes />} />
              <Route path={routes.adminActions} element={<AdminActions />} />
              <Route path={routes.loanData} element={<LoanData />} />
              <Route path={routes.reports} element={<Reports />} />
              <Route path={routes.platformAccounts} element={<PlatformAccounts />} />
              <Route path={routes.ledgerTransactions} element={<LedgerTransactions />} />
              <Route path={`${routes.ledgerTransactions}/:entryId`} element={<LedgerTransactionDetails />} />
            </Route>

            <Route element={<DevRoute />}>
              <Route path={routes.statementStudio} element={<StatementStudio />} />
              <Route path={`${routes.statementStudio}/:jobId/:loanId`} element={<StatementStudioJobDetails />} />
              <Route path={routes.timelineRuns} element={<TimelineRuns />} />
              <Route path={routes.obanJobs} element={<ObanJobs />} />
              <Route path={`${routes.obanJobs}/:jobId`} element={<ObanJobDetails />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </AppRoutesProvider>
  );
};
